/**
 * SOVRANOVA — Side Menu (slide-out drawer)
 * The home for everything: Core features + the 25 healing Modules.
 * Built modules are tappable; unbuilt ones show "Coming soon" and flip to
 * live as each is built. This is the scalable home for the full platform.
 */
import { useEffect } from 'react'

// Adult (18+) Intimacy module — only listed when explicitly enabled at build
// time. Hidden in the production kids-inclusive build (flag unset = false).
const FEATURE_INTIMACY = import.meta.env.VITE_FEATURE_INTIMACY === 'true'
// Family / guardian linking is OFF for beta wave 1 (4 Sep 2026) — see server.js.
const FEATURE_FAMILY = import.meta.env.VITE_FEATURE_FAMILY === 'true'
const FEATURE_METABOLIC = import.meta.env.VITE_FEATURE_METABOLIC === 'true'
const FEATURE_FOCUS = import.meta.env.VITE_FEATURE_FOCUS === 'true'
const FEATURE_MEDITATION = import.meta.env.VITE_FEATURE_MEDITATION === 'true'
const FEATURE_VITALITY = import.meta.env.VITE_FEATURE_VITALITY === 'true'
const FEATURE_COUPLES = import.meta.env.VITE_FEATURE_COUPLES === 'true'
const FEATURE_AMBIENT = import.meta.env.VITE_FEATURE_AMBIENT === 'true'
// ENGINE (Sprint-2 — clinician-gated psychoeducation modules) — only listed
// when explicitly enabled at build time; matches App.jsx's own gate.
const FEATURE_ENGINE = import.meta.env.VITE_FEATURE_ENGINE === 'true'
// REVIEW CONSOLE (Sprint-2 Task 6 — clinician approve/reject queue) — only
// listed when explicitly enabled at build time; matches App.jsx's own gate.
const FEATURE_REVIEW_CONSOLE = import.meta.env.VITE_FEATURE_REVIEW_CONSOLE === 'true'
// FIND PROFESSIONAL SUPPORT (Sprint-2 S3 W11 — referral directories v0) — only
// listed when explicitly enabled at build time; matches App.jsx's own gate.
const FEATURE_REFERRAL = import.meta.env.VITE_FEATURE_REFERRAL === 'true'
// FEEDBACK (in-app, worldwide, voice-or-text) — listed only when enabled at
// build time; matches App.jsx's gate and the backend FEATURE_FEEDBACK mount.
const FEATURE_FEEDBACK = import.meta.env.VITE_FEATURE_FEEDBACK === 'true'
// SLEEP PROTOCOL (8-hour tone) — listed only when enabled at build time.
// Disabled for beta wave 1 (pre-beta audio/claims audit, 4 Sep 2026, D-A);
// matches App.jsx's gate. Scripted sleep modes live inside Guided Sessions /
// Meditation and are unaffected.
const FEATURE_SLEEP_PROTOCOL = import.meta.env.VITE_FEATURE_SLEEP_PROTOCOL === 'true'
// Hume EVI voice — CONTAINED for beta wave 1 (H1, 16 Sep 2026). MASCULINE,
// FEMININE and SOVEREIGN FLOW are Hume-only; hidden unless the build sets it.
const FEATURE_HUME_VOICE = import.meta.env.VITE_FEATURE_HUME_VOICE === 'true'
// (FEATURE_FAMILY is declared above with the other module gates — hidden for
// beta wave 1, 18+ only and closed. It mirrors the App.jsx route gate; both
// must agree or the entry dead-ends. Declared once, deliberately: the
// Individual/VENT and User-Journey waves both introduced this gate.)

const CORE = [
  { id: 'dashboard',    emoji: '🧭', name: 'Journey' },
  { id: 'session',      emoji: '💬', name: 'AI Session' },
  { id: 'vent',         emoji: '🎙', name: 'Vent' },
  { id: 'hypnosis',     emoji: '🧠', name: 'Guided Sessions' },
  { id: 'journal',      emoji: '📖', name: 'Journal' },
  { id: 'sleep',        emoji: '🌙', name: 'Sleep', sleepProtocolFlag: true },
  { id: 'affirmations', emoji: '✨', name: 'Affirmations' },
  { id: 'engine',       emoji: '🗺️', name: 'Guided Paths', engineFlag: true },
  { id: 'review-console', emoji: '🩺', name: 'Review Console', reviewConsoleFlag: true },
  { id: 'feedback',     emoji: '💬', name: 'Feedback', feedbackFlag: true },
]

// Modules — live:true are built. The rest show "Soon" until built.
const MODULES = [
  { id: 'masculine',      emoji: '🛡️', name: 'MASCULINE',      desc: "Men's wellbeing", live: true, humeFlag: true },
  { id: 'feminine',       emoji: '🌷', name: 'FEMININE',       desc: "Women's wellbeing", live: true, humeFlag: true },
  { id: 'sovereign_flow', emoji: '🌊', name: 'SOVEREIGN FLOW', desc: 'Money & abundance', live: true, humeFlag: true },
  { id: 'anchor',         emoji: '⚓', name: 'ANCHOR',         desc: 'Steadiness & pacing', live: true },
  { id: 'apex',           emoji: '⛰️', name: 'APEX',           desc: 'Human potential' },
  { id: 'restore',        emoji: '🌿', name: 'RESTORE',        desc: 'Burnout & recovery' },
  { id: 'renew',          emoji: '🔄', name: 'RENEW',          desc: 'Addiction & habits' },
  { id: 'sacred',         emoji: '🕊️', name: 'SACRED',         desc: 'Grief & loss' },
  { id: 'phoenix',        emoji: '🔥', name: 'PHOENIX',        desc: 'Life transitions' },
  { id: 'kinship',        emoji: '👨‍👩‍👧', name: 'KINSHIP',    desc: 'Family & relationships' },
  { id: 'echo',           emoji: '🔊', name: 'ECHO',           desc: 'Narcissistic abuse recovery' },
  { id: 'create',         emoji: '🎨', name: 'CREATE',         desc: 'Expressive arts' },
  { id: 'lumina',         emoji: '💤', name: 'LUMINA',         desc: 'Sleep & dreams' },
  { id: 'nourish',        emoji: '🍃', name: 'NOURISH',        desc: 'Gut-brain & food' },
  { id: 'terra',          emoji: '🌍', name: 'TERRA',          desc: 'Eco-anxiety' },
  { id: 'vitality',       emoji: '🏃', name: 'VITALITY',       desc: 'Movement & fitness', live: true, vitalityFlag: true },
  { id: 'brain',          emoji: '🧬', name: 'BRAIN',          desc: 'Neuroscience education' },
  { id: 'play',           emoji: '🎈', name: 'PLAY',           desc: 'Joy & humour' },
  { id: 'bloom',          emoji: '🌸', name: 'BLOOM',          desc: 'Kids 8-12' },
  { id: 'guardian',       emoji: '🛟', name: 'GUARDIAN',       desc: 'Teen safety' },
  { id: 'intimacy',       emoji: '🌙', name: 'INTIMACY',       desc: 'Connection & wellbeing', live: true, adultFlag: true },
  { id: 'metabolic',      emoji: '⏳', name: 'METABOLIC',      desc: 'Calm eating & fasting', live: true, metabolicFlag: true },
  { id: 'focus',          emoji: '🎯', name: 'FOCUS',          desc: 'ADHD & task initiation', live: true, focusFlag: true },
  // "Yoga Nidra" is named here on purpose — it lives inside this hub and was
  // unfindable by name from the nav (reported 19 Aug).
  { id: 'meditation',     emoji: '🧘', name: 'MEDITATION',     desc: 'Mindfulness · Yoga Nidra', live: true, meditationFlag: true },
  { id: 'couples',        emoji: '💞', name: 'COUPLES',        desc: 'Relationship practice', live: true, couplesFlag: true },
  { id: 'ambient',        emoji: '🪞', name: 'REFLECT',        desc: 'Debrief a session', live: true, ambientFlag: true },
]

export function SideMenu({ open, onClose, onNavigate, currentView, blocked = new Set() }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    if (open) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      <div style={{ ...m.overlay, opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none' }} onClick={onClose} />
      <aside style={{ ...m.drawer, transform: open ? 'translateX(0)' : 'translateX(-100%)' }}>
        <div style={m.head}>
          <span style={m.brand}>◎ SOVRANOVA</span>
          <button style={m.close} onClick={onClose} aria-label="Close menu">×</button>
        </div>

        <div style={m.scroll}>
          <p style={m.groupLabel}>Core</p>
          {CORE.filter(item => (!item.engineFlag || FEATURE_ENGINE) && (!item.reviewConsoleFlag || FEATURE_REVIEW_CONSOLE) && (!item.feedbackFlag || FEATURE_FEEDBACK) && (!item.sleepProtocolFlag || FEATURE_SLEEP_PROTOCOL)).map(item => {
            const isBlocked = blocked.has(item.id)
            return (
            <button key={item.id}
              style={{ ...m.row, ...(currentView === item.id ? m.rowActive : {}), ...(isBlocked ? m.rowSoon : {}) }}
              onClick={() => { if (!isBlocked) { onNavigate(item.id); onClose() } }}
              disabled={isBlocked}>
              <span style={m.rowEmoji}>{item.emoji}</span>
              <span style={m.rowName}>{item.name}</span>
              {isBlocked && <span style={m.soonTag}>🔒</span>}
            </button>
          )})}

          <p style={m.groupLabel}>Healing Modules</p>
          {/* LIVE MODULES ONLY (19 Aug): unbuilt "Soon" rows are hidden rather
              than greyed — a menu of mostly-disabled promises made the working
              features harder to find and read as clutter to real users. The
              full MODULES list stays above as the roadmap; a module reappears
              here the moment it ships (live: true). */}
          {MODULES.filter(mod => mod.live && (!mod.adultFlag || FEATURE_INTIMACY) && (!mod.metabolicFlag || FEATURE_METABOLIC) && (!mod.focusFlag || FEATURE_FOCUS) && (!mod.meditationFlag || FEATURE_MEDITATION) && (!mod.couplesFlag || FEATURE_COUPLES) && (!mod.ambientFlag || FEATURE_AMBIENT) && (!mod.vitalityFlag || FEATURE_VITALITY) && (!mod.humeFlag || FEATURE_HUME_VOICE)).map(mod => {
            const isBlocked = blocked.has(mod.id)
            const usable = !isBlocked
            return (
            <button key={mod.id}
              style={{ ...m.row, ...(usable ? {} : m.rowSoon) }}
              onClick={() => { if (usable) { onNavigate(mod.id); onClose() } }}
              disabled={!usable}>
              <span style={m.rowEmoji}>{mod.emoji}</span>
              <span style={m.modText}>
                <span style={m.rowName}>{mod.name}</span>
                <span style={m.modDesc}>{mod.desc}</span>
              </span>
              {isBlocked && <span style={m.soonTag}>🔒</span>}
            </button>
          )})}
        </div>

        <button style={m.settingsRow} onClick={() => { onNavigate('handover'); onClose() }}>📋 Summary for a professional</button>
        {FEATURE_REFERRAL && (
          <button style={m.settingsRow} onClick={() => { onNavigate('referral'); onClose() }}>🧭 Find professional support</button>
        )}
        {FEATURE_FAMILY && (
          <button style={m.settingsRow} onClick={() => { onNavigate('family'); onClose() }}>👪 Family &amp; parental controls</button>
        )}
        <button style={m.settingsRow} onClick={() => { onNavigate('guide'); onClose() }}>📖 How to use SOVRANOVA</button>
        <button style={m.settingsRow} onClick={() => { onNavigate('about'); onClose() }}>ⓘ About SOVRANOVA</button>
        <button style={m.settingsRow} onClick={() => { onNavigate('settings'); onClose() }}>⚙ Settings</button>
      </aside>
    </>
  )
}

const m = {
  overlay:    { position:'fixed', inset:0, background:'rgba(0,0,0,.5)', backdropFilter:'blur(2px)', zIndex:998, transition:'opacity 0.25s' },
  drawer:     { position:'fixed', top:0, left:0, bottom:0, width:'min(82vw,320px)', background:'linear-gradient(165deg,#161922,#0e1119)', zIndex:999, display:'flex', flexDirection:'column', boxShadow:'4px 0 30px rgba(0,0,0,.5)', transition:'transform 0.28s cubic-bezier(.4,0,.2,1)' },
  head:       { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1.1rem 1.2rem', borderBottom:'1px solid rgba(255,255,255,.08)' },
  brand:      { fontSize:'0.95rem', fontWeight:800, color:'#f0f4f8', letterSpacing:'0.04em' },
  close:      { background:'transparent', border:'none', color:'rgba(255,255,255,.6)', fontSize:'1.6rem', cursor:'pointer', lineHeight:1 },
  scroll:     { flex:1, overflowY:'auto', padding:'0.6rem 0.7rem' },
  groupLabel: { fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(240,244,248,.35)', fontWeight:700, padding:'0.9rem 0.6rem 0.4rem' },
  row:        { display:'flex', alignItems:'center', gap:'0.7rem', width:'100%', background:'transparent', border:'none', borderRadius:'0.7rem', padding:'0.7rem 0.6rem', cursor:'pointer', textAlign:'left', color:'#e8eef4' },
  rowActive:  { background:'rgba(139,92,246,.18)' },
  rowSoon:    { opacity:0.5, cursor:'default' },
  rowEmoji:   { fontSize:'1.25rem', width:'1.6rem', textAlign:'center', flexShrink:0 },
  rowName:    { fontSize:'0.9rem', fontWeight:600, color:'#f0f4f8' },
  modText:    { display:'flex', flexDirection:'column', flex:1, minWidth:0 },
  modDesc:    { fontSize:'0.68rem', color:'rgba(240,244,248,.45)' },
  soonTag:    { fontSize:'0.6rem', fontWeight:700, color:'rgba(240,244,248,.5)', background:'rgba(255,255,255,.07)', borderRadius:'0.5rem', padding:'0.15rem 0.45rem', letterSpacing:'0.05em' },
  settingsRow:{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'1rem 1.2rem', background:'rgba(255,255,255,.03)', border:'none', borderTop:'1px solid rgba(255,255,255,.08)', color:'#d8e2ec', fontSize:'0.875rem', fontWeight:600, cursor:'pointer', textAlign:'left' },
}
